/**
 * useSaveBid — create or update a saved bid.
 *
 * Insert when `id` is absent, update when present. The computed money/
 * time figures are snapshotted into dedicated columns from the supplied
 * `BidComputation` so the saved row never has to re-run the engine. The
 * engine inputs go to `inputs_json`.
 *
 * RLS scopes writes to contractor_admin / super_admin in the caller's
 * org. On insert we stamp organization_id and created_by_user_id from
 * the JWT (app_metadata) so WITH CHECK passes; a non-admin gets a
 * PostgREST 401/403. Invalidates the `bids` list on success.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase }                  from './useSupabase'
import { useOrgId, useUserId }          from './useAuth'
import type { BidInputs, BidComputation } from '@/lib/bidding/engine'
import type { BidRow, BidStatus }       from './useBids'

export interface SaveBidArgs {
  /** Present → update that bid; absent → insert a new one. */
  id?:          string
  title:        string
  clientName:   string | null
  jobType:      string
  status:       BidStatus
  notes:        string | null
  inputs:       BidInputs
  computation:  BidComputation
}

export function useSaveBid(): ReturnType<typeof useMutation<BidRow, Error, SaveBidArgs>> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()
  const orgId       = useOrgId()
  const userId      = useUserId()

  return useMutation<BidRow, Error, SaveBidArgs>({
    mutationFn: async (args) => {
      const snapshot = {
        title:               args.title.trim(),
        client_name:         args.clientName?.trim() ? args.clientName.trim() : null,
        job_type:            args.jobType,
        status:              args.status,
        notes:               args.notes?.trim() ? args.notes.trim() : null,
        inputs_json:         args.inputs as unknown as Record<string, number>,
        unit_count:          args.computation.unitCount,
        total_labor_minutes: Math.round(args.computation.totalLaborMinutes),
        total_cost_cents:    args.computation.totalCostCents,
        total_bid_cents:     args.computation.suggestedBidCents,
      }

      if (args.id) {
        const { data, error } = await supabase
          .from('bids')
          .update(snapshot)
          .eq('id', args.id)
          .select()
          .single()
        if (error) throw error
        return data as unknown as BidRow
      }

      if (!orgId) throw new Error('NO_ORG_CONTEXT')

      const { data, error } = await supabase
        .from('bids')
        .insert({ ...snapshot, organization_id: orgId, created_by_user_id: userId })
        .select()
        .single()
      if (error) throw error
      return data as unknown as BidRow
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bids'] })
    },
  })
}
